import type { PostInput, PostType, PostVisibility, PrPayload } from '@fitness/api-client';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Send } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { PageHeader } from '@/components/PageHeader';
import { Textarea } from '@/components/exercise/Textarea';
import { RecipeAttachment, RoutineAttachment } from '@/components/community/AttachmentPicker';
import { ComposeImage } from '@/components/community/ComposeImage';
import { SharedPrCard } from '@/components/community/PostAttachment';
import { POST_VISIBILITY_LABEL } from '@/components/community/format';
import { composeSchema, type ComposeSearch } from '@/components/community/searchParams';
import { useCreatePost } from '@/lib/queries/posts';

/** Tope del contrato para el texto de una publicación. */
const MAX_TEXT = 2000;

export const Route = createFileRoute('/_app/community/compose')({
  validateSearch: composeSchema,
  component: ComposePage,
});

/** Los tres tipos que se eligen a mano; `workout` y `pr` llegan ya decididos. */
const MANUAL_TYPES: ReadonlyArray<{ value: PostType; label: string }> = [
  { value: 'text', label: 'Texto' },
  { value: 'routine_share', label: 'Rutina' },
  { value: 'recipe_share', label: 'Receta' },
];

/** Reconstruye la marca personal que viene desglosada en la URL. */
function prFromSearch(search: ComposeSearch): PrPayload | null {
  const { prExerciseId, prWeightKg, prReps } = search;
  if (prExerciseId === undefined || prWeightKg === undefined || prReps === undefined) return null;
  return {
    exerciseId: prExerciseId,
    exerciseName: search.prExerciseName ?? 'Ejercicio',
    weightKg: prWeightKg,
    reps: prReps,
    // El servidor la recalcula con Epley; esto es sólo para la previsualización.
    estimated1RmKg: search.prEstimated1RmKg ?? prWeightKg * (1 + prReps / 30),
    performedAt: search.prPerformedAt ?? new Date().toISOString(),
  };
}

function initialType(search: ComposeSearch): PostType {
  if (search.type !== undefined) return search.type;
  if (search.routineId !== undefined) return 'routine_share';
  if (search.recipeId !== undefined) return 'recipe_share';
  if (search.workoutSessionId !== undefined) return 'workout';
  return 'text';
}

/**
 * Publicar.
 *
 * El tipo puede llegar decidido desde otra pantalla (compartir una rutina, una
 * receta, el entrenamiento recién terminado o una marca), y entonces no se
 * ofrece cambiarlo: lo que se comparte ya está elegido y lo único que falta es
 * el texto.
 */
function ComposePage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [type, setType] = useState<PostType>(() => initialType(search));
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<PostVisibility>('public');
  const [routineId, setRoutineId] = useState<string | null>(search.routineId ?? null);
  const [recipeId, setRecipeId] = useState<string | null>(search.recipeId ?? null);
  const [shareable, setShareable] = useState(false);

  const create = useCreatePost();
  const pr = prFromSearch(search);
  const workoutSessionId = search.workoutSessionId ?? null;
  const fixed = type === 'workout' || type === 'pr';

  // El picker avisa de si el recurso elegido ya se puede compartir; el `set`
  // de `useState` es estable, así que el efecto de allí no se reinicia solo.
  const onShareableChange = useCallback((value: boolean) => setShareable(value), []);

  const missing =
    type === 'routine_share' && (routineId === null || !shareable)
      ? 'Elige una rutina que se pueda compartir.'
      : type === 'recipe_share' && (recipeId === null || !shareable)
        ? 'Elige una receta que se pueda compartir.'
        : type === 'workout' && workoutSessionId === null
          ? 'Falta la sesión de entrenamiento.'
          : type === 'pr' && pr === null
            ? 'Faltan los datos de la marca personal.'
            : type === 'text' && text.trim() === '' && imageUrl === null
              ? 'Escribe algo o añade una imagen.'
              : null;

  function publish(): void {
    if (missing !== null) return;
    const trimmed = text.trim();
    const body: PostInput = {
      type,
      visibility,
      ...(trimmed === '' ? {} : { text: trimmed.slice(0, MAX_TEXT) }),
      ...(imageUrl === null ? {} : { imageUrl }),
      ...(type === 'routine_share' && routineId !== null ? { routineId } : {}),
      ...(type === 'recipe_share' && recipeId !== null ? { recipeId } : {}),
      ...(type === 'workout' && workoutSessionId !== null ? { workoutSessionId } : {}),
      ...(type === 'pr' && pr !== null ? { pr } : {}),
    };
    create.mutate(body, {
      onSuccess: () => void navigate({ to: '/community', search: {} }),
    });
  }

  return (
    <>
      <PageHeader
        title="Publicar"
        action={
          <Link
            to="/community"
            search={{}}
            aria-label="Volver a la comunidad"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4 pb-6">
        {fixed ? null : (
          <div className="flex gap-1 rounded-xl border border-line bg-surface-raised p-1">
            {MANUAL_TYPES.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={type === option.value}
                onClick={() => setType(option.value)}
                className={
                  type === option.value
                    ? 'min-h-11 flex-1 rounded-lg bg-brand text-sm font-semibold text-brand-ink'
                    : 'min-h-11 flex-1 rounded-lg text-sm font-medium text-ink-muted transition-colors hover:bg-surface-overlay active:bg-surface-overlay'
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        <Field
          htmlFor="post-text"
          label={type === 'text' ? 'Qué quieres contar' : 'Un comentario (opcional)'}
          hint={`${text.length}/${MAX_TEXT}`}
        >
          <Textarea
            {...fieldAria('post-text', undefined, true)}
            rows={4}
            maxLength={MAX_TEXT}
            value={text}
            placeholder={type === 'text' ? 'Cómo ha ido el día…' : 'Por qué lo compartes'}
            onChange={(event) => setText(event.target.value)}
          />
        </Field>

        {type === 'routine_share' ? (
          <RoutineAttachment
            selectedId={routineId}
            onSelect={setRoutineId}
            onShareableChange={onShareableChange}
          />
        ) : null}

        {type === 'recipe_share' ? (
          <RecipeAttachment
            selectedId={recipeId}
            onSelect={setRecipeId}
            onShareableChange={onShareableChange}
          />
        ) : null}

        {type === 'pr' && pr !== null ? <SharedPrCard pr={pr} /> : null}

        {type === 'workout' && workoutSessionId !== null ? (
          <p className="rounded-xl border border-line bg-surface p-3 text-sm text-ink-muted">
            Se publicará el resumen de tu último entrenamiento: series, volumen y duración.
          </p>
        ) : null}

        <ComposeImage value={imageUrl} onChange={setImageUrl} disabled={create.isPending} />

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-ink">Quién puede verlo</legend>
          {(['public', 'followers'] as const).map((option) => (
            <label
              key={option}
              className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-line bg-surface px-3"
            >
              <input
                type="radio"
                name="visibility"
                checked={visibility === option}
                onChange={() => setVisibility(option)}
                className="size-4 accent-[var(--color-brand)]"
              />
              <span className="text-sm text-ink">{POST_VISIBILITY_LABEL[option]}</span>
            </label>
          ))}
        </fieldset>

        {create.isError ? (
          <ErrorBanner
            title="No se ha podido publicar"
            error={create.error}
            errorOptions={{ saving: true }}
          />
        ) : null}

        {missing !== null ? <p className="text-sm text-ink-muted">{missing}</p> : null}

        <Button
          size="lg"
          block
          loading={create.isPending}
          disabled={missing !== null}
          icon={<Send className="size-4" />}
          onClick={publish}
        >
          Publicar
        </Button>
      </div>
    </>
  );
}
